// Phase 26A · Favorites row for the dashboard.
// Renders pinned favorites as a horizontally-scrollable / draggable row.
// Uses HTML5 drag-and-drop (no extra dep).
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Star, X, GripVertical, Loader2 } from "lucide-react";
import { useFavorites } from "@/lib/favorites";

export default function FavoritesRow() {
  const navigate = useNavigate();
  const { items, loading, error, remove, reorder } = useFavorites();
  const [dragKey, setDragKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);

  const onDragStart = (key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch (_) {}
  };
  const onDragOver = (key) => (e) => {
    e.preventDefault();
    if (key !== dragKey) setHoverKey(key);
  };
  const onDrop = (targetKey) => async (e) => {
    e.preventDefault();
    setHoverKey(null);
    if (!dragKey || dragKey === targetKey) return;
    const keys = items.map((f) => f.item_key);
    const from = keys.indexOf(dragKey);
    const to = keys.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    const next = [...keys];
    next.splice(from, 1);
    next.splice(to, 0, dragKey);
    setDragKey(null);
    await reorder(next);
  };
  const onDragEnd = () => {
    setDragKey(null);
    setHoverKey(null);
  };

  return (
    <section data-testid="favorites-row" className="rounded-2xl border border-neutral-200 bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 fill-[#E5197F] text-[#E5197F]" />
          <h2 className="text-sm font-semibold text-neutral-800 tracking-tight">
            Your favorites
          </h2>
        </div>
        <span
          className="text-[11px] uppercase tracking-widest text-neutral-400"
          data-testid="favorites-count"
        >
          {items.length} pinned
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading favorites…
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 py-2" data-testid="favorites-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-neutral-500 py-2" data-testid="favorites-empty">
          Star a report or screen (★ button in the page header) to pin it here for one-tap access.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="favorites-list">
          {items.map((f) => {
            const isDragging = dragKey === f.item_key;
            const isHover = hoverKey === f.item_key;
            return (
              <div
                key={f.item_key}
                draggable
                onDragStart={onDragStart(f.item_key)}
                onDragOver={onDragOver(f.item_key)}
                onDrop={onDrop(f.item_key)}
                onDragEnd={onDragEnd}
                data-testid={`favorite-${f.item_key}`}
                className={`group flex items-center gap-2 pl-2 pr-1 h-9 rounded-full border transition-all cursor-grab active:cursor-grabbing ${
                  isDragging
                    ? "opacity-40 border-[#6D2C91]"
                    : isHover
                    ? "border-[#E5197F] bg-[#E5197F]/5"
                    : "border-neutral-200 bg-neutral-50 hover:bg-white hover:border-[#6D2C91]"
                }`}
              >
                <GripVertical className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-500" />
                <button
                  type="button"
                  onClick={() => navigate(f.item_route)}
                  data-testid={`favorite-open-${f.item_key}`}
                  className="text-sm text-neutral-800 hover:text-[#6D2C91] transition-colors max-w-[200px] truncate"
                >
                  {f.item_label}
                </button>
                <button
                  type="button"
                  onClick={() => remove(f.item_key)}
                  data-testid={`favorite-remove-${f.item_key}`}
                  className="h-6 w-6 flex items-center justify-center rounded-full text-neutral-400 hover:text-red-600 hover:bg-white"
                  aria-label="Remove favorite"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
